/**
 * Service worker registration and update coordination.
 *
 * The update flow is explicit: a waiting worker is reported to the application, which asks
 * the player before reloading. Progress is written before the reload, so an update can
 * never cost a partially played level.
 */

export type UpdateListener = (registration: ServiceWorkerRegistration) => void;

let waitingRegistration: ServiceWorkerRegistration | null = null;
const listeners = new Set<UpdateListener>();

export function onUpdateAvailable(listener: UpdateListener): () => void {
  listeners.add(listener);
  if (waitingRegistration !== null) listener(waitingRegistration);
  return () => listeners.delete(listener);
}

function announce(registration: ServiceWorkerRegistration): void {
  waitingRegistration = registration;
  for (const listener of listeners) listener(registration);
}

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  globalThis.addEventListener('load', () => {
    void navigator.serviceWorker
      .register(`${import.meta.env.BASE_URL}sw.js`, { scope: import.meta.env.BASE_URL })
      .then((registration) => {
        if (registration.waiting !== null) announce(registration);

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (installing === null) return;
          installing.addEventListener('statechange', () => {
            // A worker that reaches "installed" while another controls the page is an
            // update waiting for permission to take over.
            if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
              announce(registration);
            }
          });
        });
      })
      .catch(() => {
        // Registration failure only costs offline support; the game still runs.
      });
  });
}

let reloading = false;

/** Activates the waiting worker and reloads once it takes control. */
export function applyUpdate(registration: ServiceWorkerRegistration): void {
  const waiting = registration.waiting;
  if (waiting === null) {
    globalThis.location.reload();
    return;
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    globalThis.location.reload();
  });
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

export function isStandalone(): boolean {
  if (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  return (navigator as unknown as { standalone?: boolean }).standalone === true;
}
